import { useEffect, useId, useState } from "react";
import clsx from "clsx";
import { ExportBar } from "./ExportBar";
import { Button } from "../ui/Button";
import { selectHasUnexportedData, selectIsPaused } from "../../engine/selectors";
import { t } from "../../i18n/strings";
import { api, ApiError } from "../../api/client";
import { buildFieldSummary } from "../../state/useInterviewRunner";
import type { DataPolicy, HealthResponse, SummaryResponse } from "../../api/client";
import type { InterviewState } from "../../engine/types";

const APP_VERSION = "0.1.0";
const GOOGLE_TERMS_URL = "https://ai.google.dev/gemini-api/terms";

// Fixed panel copy per §13. The default (`unspecified`) is deliberately NOT
// reassuring — an operator who hasn't declared anything should see an
// honest "unknown," never a calm default. Keys, not literals, so both
// locales stay faithful translations rather than paraphrases (see strings.ts).
const DATA_POLICY_KEY: Record<
  Exclude<DataPolicy, "custom">,
  { key: "governance.dataPolicy.unspecified" | "governance.dataPolicy.paidTier" | "governance.dataPolicy.freeTier"; warn?: boolean }
> = {
  unspecified: { key: "governance.dataPolicy.unspecified" },
  paid_tier_no_training: { key: "governance.dataPolicy.paidTier" },
  free_tier_may_train: { key: "governance.dataPolicy.freeTier", warn: true },
};

export function GovernancePanel({
  state,
  health,
  onSafetyPause,
}: {
  state: InterviewState;
  health: HealthResponse | null;
  /** Called when /summary returns the paused state, so the check-in adopts the paused token (round 4). */
  onSafetyPause?: (paused: Extract<SummaryResponse, { summary: null }>) => void;
}) {
  const [open, setOpen] = useState(true);
  const [exported, setExported] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryProvider, setSummaryProvider] = useState<string | null>(null);
  const [summaryStatus, setSummaryStatus] = useState<"idle" | "loading" | "error" | "blocked">("idle");
  const panelId = useId();
  const hasUnexported = selectHasUnexportedData(state) && !exported;
  const locale = state.locale;
  // Non-negotiable: no model call after a safety pause. The summary is a
  // model call, so the button is disabled while paused (the server also
  // refuses, via the continuity token) and until a token exists to send.
  const isPaused = selectIsPaused(state);
  const summaryUnavailable = isPaused || !state.continuityToken;

  async function generateSummary() {
    if (summaryUnavailable || !state.continuityToken) return;
    setSummaryStatus("loading");
    try {
      const res = await api.summary({
        locale,
        field_summary: buildFieldSummary(state),
        continuity_token: state.continuityToken,
        participant_turn_count: state.meta.turnCount, // same source as /turn and /followup
        template: state.template,
      });
      if (res.summary === null) {
        // A disclosure in the values, or a model refusal: the server paused the
        // check-in. Adopt the paused state; never show a summary.
        onSafetyPause?.(res);
        setSummaryStatus("blocked");
        return;
      }
      setSummary(res.summary);
      setSummaryProvider(res.meta.provider);
      setSummaryStatus("idle");
    } catch (e) {
      // 409 check_in_paused: the server refused without a model call. Show the
      // pause line, not the generic "try again" error. (safety_hold is the
      // pre-round-4 code for a disclosure in the values; still treated the same.)
      const blocked = e instanceof ApiError && (e.code === "check_in_paused" || e.code === "safety_hold");
      setSummaryStatus(blocked ? "blocked" : "error");
    }
  }

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasUnexported) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnexported]);

  const policy = health?.data_policy ?? "unspecified";
  const isCustom = policy === "custom";
  // Custom policy text is operator-declared verbatim (§13) — never translated by Fieldnote.
  const copyText = isCustom ? health?.data_policy_text ?? "" : t(locale, DATA_POLICY_KEY[policy].key);
  const warnTint = !isCustom && DATA_POLICY_KEY[policy].warn;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-controls={panelId}
        className="w-full rounded-lg border border-border bg-paper-dim px-3 py-1.5 text-left text-xs text-ink-soft hover:text-ink"
      >
        {t(locale, "governance.summary.template", {
          mode: health?.mode === "live" ? t(locale, "governance.summary.live") : t(locale, "governance.summary.demo"),
        })}
      </button>
    );
  }

  return (
    <div
      id={panelId}
      className={clsx(
        "space-y-3 rounded-lg border p-4 text-sm",
        warnTint ? "border-warn/50 bg-warn-dim" : "border-border bg-paper-dim",
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base text-ink">{t(locale, "governance.heading")}</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-expanded={true}
          aria-controls={panelId}
          className="text-xs text-ink-soft hover:text-ink"
        >
          {t(locale, "governance.collapse")}
        </button>
      </div>

      <p className="text-ink">{t(locale, "governance.persistence")}</p>

      <p className="text-ink">{t(locale, "governance.liveDataStatement")}</p>

      {/* text-ink rather than text-warn on bg-warn-dim: the pair measures
          ~3.98:1, short of the 4.5:1 AA text threshold (see Pill.tsx). The
          warm tint + border still carry the warning; only the text color
          changes, and the shared --color-warn token is untouched. */}
      <p className={clsx(warnTint ? "font-medium text-ink" : "text-ink-soft")}>
        {copyText}{" "}
        {!isCustom && (
          <a href={GOOGLE_TERMS_URL} className="underline" target="_blank" rel="noreferrer">
            {t(locale, "governance.googleTermsLink")}
          </a>
        )}
      </p>
      {isCustom && <p className="text-xs italic text-ink-soft">{t(locale, "governance.customDeclared")}</p>}

      <div className="space-y-2 border-t border-border pt-3">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={generateSummary} disabled={summaryUnavailable || summaryStatus === "loading"}>
            {summaryStatus === "loading" ? t(locale, "summary.generating") : t(locale, "summary.generateButton")}
          </Button>
          {summary && (
            <span className="text-xs font-medium uppercase tracking-wide text-clay">
              {t(locale, summaryProvider === "demo" ? "summary.badgeDemo" : "summary.badge")}
            </span>
          )}
        </div>
        {(isPaused || summaryStatus === "blocked") && (
          <p className="text-xs text-ink-soft">{t(locale, "summary.pausedNote")}</p>
        )}
        {summaryStatus === "error" && <p className="text-xs text-danger">{t(locale, "summary.error")}</p>}
        {summary && <p className="text-sm italic text-ink">{summary}</p>}
      </div>

      <ExportBar
        state={state}
        meta={{
          appVersion: APP_VERSION,
          provider: state.meta.provider ?? health?.mode ?? "demo",
          locale: state.locale,
          dataPolicy: policy,
        }}
        summary={summary}
        onExported={() => setExported(true)}
      />

      <p className="text-xs text-ink-soft">{t(locale, "governance.cabNote")}</p>
    </div>
  );
}
